import { parse as parseYaml, stringify as stringifyYaml } from "yaml";

export interface ParsedCustomSkillMarkdown {
  name: string;
  description: string;
  instructions: string;
  snippet: string;
}

export const parseCustomSkillMarkdown = (
  markdown: string,
): ParsedCustomSkillMarkdown => {
  if (new TextEncoder().encode(markdown).length > 32 * 1024) {
    throw new Error("Each skill file must be 32 KB or smaller.");
  }

  const lines = markdown
    .replace(/\r\n/g, "\n")
    .replace(/^\uFEFF/, "")
    .split("\n");
  if (lines[0]?.trim() !== "---") {
    throw new Error("The file must start with YAML frontmatter (---).");
  }
  const closingIndex = lines.findIndex(
    (line, index) => index > 0 && line.trim() === "---",
  );
  if (closingIndex < 0) {
    throw new Error("The YAML frontmatter is missing its closing ---.");
  }

  let metadata: unknown;
  try {
    metadata = parseYaml(lines.slice(1, closingIndex).join("\n"));
  } catch {
    throw new Error("The YAML frontmatter is invalid.");
  }
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    throw new Error(
      "The YAML frontmatter must contain name and description fields.",
    );
  }

  const fields = metadata as Record<string, unknown>;
  const name = typeof fields.name === "string" ? fields.name : "";
  const description =
    typeof fields.description === "string" ? fields.description.trim() : "";
  if (!name) throw new Error("The frontmatter must include a name.");
  if (!description) {
    throw new Error("The frontmatter must include a description.");
  }

  const instructions = lines
    .slice(closingIndex + 1)
    .join("\n")
    .trim();
  if (!instructions) {
    throw new Error(
      "The file must include instructions below the frontmatter.",
    );
  }
  return {
    name,
    description,
    instructions,
    snippet: instructions.split("\n").slice(0, 5).join("\n"),
  };
};

export const composeCustomSkillMarkdown = (
  markdown: string,
  name: string,
): string => {
  const parsed = parseCustomSkillMarkdown(markdown);
  const frontmatter = stringifyYaml({
    name,
    description: parsed.description,
  }).trimEnd();
  return `---\n${frontmatter}\n---\n\n${parsed.instructions}\n`;
};
